import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, parseApiError, type CreateClanInput } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { usePageActions } from '../lib/pageActions';
import { DiscordMemberPicker } from './DiscordMemberPicker';

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

export function CreateClanForm({
  onDone,
  compId,
  defaultOpen,
}: {
  onDone: () => void;
  compId?: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const [form, setForm] = useState<CreateClanInput>({ name: '', leaderDiscordId: '', description: '' });
  const [error, setError] = useState('');
  // W3 soft-stamp: bind a bare clan create to the active workspace org.
  const { activeOrgId } = useWorkspace();

  const mut = useMutation({
    mutationFn: async (data: CreateClanInput) => {
      if (compId) {
        return api.createCompetitionClan(compId, {
          name: data.name,
          ownerDiscordId: data.leaderDiscordId || undefined,
          description: data.description || undefined,
        });
      }
      return api.createClan({ ...data, orgId: activeOrgId ?? undefined });
    },
    onSuccess: () => {
      setOpen(false);
      setForm({ name: '', leaderDiscordId: '', description: '' });
      setError('');
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Global bottom action-bar registration — opens the SAME inline
  // create-form this component already renders (the collapsed state below
  // IS the "+ New Clan" button) rather than duplicating a picker/form into
  // the bar. Always registered whenever this component is mounted — callers
  // only mount it where clan creation is actually offered (ClanWorkbench's
  // isAdminPlus gate, MyCompDetail's canManageClans gate), so no separate
  // role check is needed here (see lib/pageActions.tsx's role-awareness
  // contract). Hook must sit before the `if (!open)` early return below
  // (Rules of Hooks).
  const newClanActions = useMemo(
    () => ([{
      id: 'clan-create-new',
      label: 'New clan',
      icon: Plus,
      onClick: () => setOpen(true),
    }]),
    [],
  );
  usePageActions(newClanActions);

  if (!open)
    return (
      <button onClick={() => setOpen(true)} className="btn-primary flex items-center gap-1.5 text-sm">
        <Plus className="h-4 w-4" /> New Clan
      </button>
    );

  return (
    <div className="mt-3 rounded-xl border border-violet-500/30 bg-surface-950 p-4 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-violet-400">
        New Clan{compId ? ' (auto-enrolled)' : ''}
      </p>
      <input type="text" placeholder="Clan name" value={form.name}
        onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input w-full" />
      <div>
        <label className="label">Leader</label>
        {/* Name-first leader selection (Lisa, 2026-07-13): pick anyone from the
            org's Discord server by name — the id travels with the choice. The
            picker's built-in paste-an-ID fallback keeps the old raw-ID path for
            users outside the server or when the guild lookup is unavailable. */}
        <DiscordMemberPicker
          search={q => activeOrgId
            ? api.searchOrgDiscordMembers(activeOrgId, q)
            : Promise.resolve({ members: [] })}
          value={form.leaderDiscordId ?? ''}
          onChange={id => setForm(f => ({ ...f, leaderDiscordId: id }))}
          placeholder="Search the Discord server by name…"
        />
      </div>
      <input type="text" placeholder="Description (optional)" value={form.description}
        onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="input w-full" />
      <ErrorMsg msg={error} />
      <div className="flex gap-2">
        <button onClick={() => mut.mutate(form)} disabled={!form.name.trim() || !form.leaderDiscordId?.trim() || mut.isPending} className="btn-primary flex-1">
          {mut.isPending ? 'Creating…' : 'Create Clan'}
        </button>
        <button onClick={() => { setOpen(false); setError(''); }} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
}
